import CT from '@marketplace-ai/tags';
import debounce from 'debounce-promise';
import React from 'react';
import { Controller, FieldPath, FieldValues, PathValue, useFormContext } from 'react-hook-form';
import { GroupBase, OptionsOrGroups, SingleValue } from 'react-select';
import ReactAsyncCreatableSelect from 'react-select/async-creatable';
import { formatCreateOptionLabel } from '../createOptionLabel';
import { FormFeedback } from '../FormFeedback';
import { FormField } from '../FormField';
import { getOptionFieldError, matchesAllTokens } from '../formUtils';
import { FormFieldProps } from '../interfaces';
import { FormSelectInput, selectPortalProps, SelectStyles } from '../Select/Select';

// Matches AsyncSelect's `shouldLoadAsyncSelectOptions` threshold. This was `>`
// rather than `>=`, so a creatable async select quietly needed four characters
// where its plain sibling needed three -- enough to stop three-letter names
// (MIT, NYU) from ever searching.
const MINIMUM_CHARACTERS_SEARCH = 3;

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TOption extends { value: string | number; label: string } = {
    value: string | number;
    label: string;
  },
> extends FormFieldProps<TFieldValues, TName> {
  noOptionsMessage?: string;
  loadingMessage?: string;
  cacheOptions?: boolean;
  defaultOptions?: boolean;
  minChars?: boolean;
  // [MCV-1628] Render the dropdown menu in a portal so it escapes ancestor
  // overflow/clip boundaries (e.g. FormDialog's `overflow-y-auto` body).
  // Defaults to true: an in-flow menu is positioned `absolute` inside that
  // scroll container, so it is both clipped by it and counted in its
  // scrollable overflow -- which is what put a ~300px phantom scrollbar on
  // every dialog holding a non-portaled select. Pass `usePortal={false}`
  // only for a menu that must stay in normal document flow.
  usePortal?: boolean;
  onChange?: (option: SingleValue<TOption>) => void;
  loadOptions: (
    inputValue: string
  ) => Promise<
    OptionsOrGroups<PathValue<TFieldValues, TName>, GroupBase<PathValue<TFieldValues, TName>>>
  >;
  onCreateOption?: (inputValue: string) => void;
  // Opt in when the caller confirms duplicates before creating a new record.
  allowCreateWhileMatching?: boolean;
  // Label for the "create" row react-select appends when the typed text
  // matches no option. Defaults to `Add "<text>"`.
  formatCreateLabel?: (inputValue: string) => string;
}

export const AsyncCreatableSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TOption extends { value: string | number; label: string } = {
    value: string | number;
    label: string;
  },
>(
  props: Props<TFieldValues, TName, TOption>
) => {
  const {
    name,
    description,
    cyTag,
    label,
    loadingMessage = 'Loading...',
    noOptionsMessage = 'No Results',
    loadOptions,
    onChange,
    disabled,
    onCreateOption,
    allowCreateWhileMatching = false,
    formatCreateLabel = formatCreateOptionLabel,
    minChars = true,
    flagged,
    required,
    tooltip,
    usePortal = true,
    errorCyTag,
    reserveFeedbackSpace,
  } = props;
  const {
    formState: { errors },
  } = useFormContext();
  const debouncedLoadOptions = React.useMemo(() => debounce(loadOptions, 300), [loadOptions]);
  const error = getOptionFieldError(errors, name);
  const errorId = `${name}-error`;
  const helperId = `${name}-helper`;
  const resolvedErrorCyTag = errorCyTag ?? (cyTag ? `${cyTag}-error` : undefined);

  return (
    <Controller
      name={name}
      render={({ field }) => {
        const hasValue = (field.value && field.value.length > 0) || field.value?.label;
        return (
          <FormField
            id={name}
            label={label}
            tooltip={tooltip}
            required={required}
            feedback={
              <FormFeedback
                error={error?.message}
                errorId={errorId}
                errorCyTag={resolvedErrorCyTag}
                description={description}
                helperId={helperId}
                reserveSpace={reserveFeedbackSpace}
              />
            }
          >
            <div data-cy={cyTag}>
              <ReactAsyncCreatableSelect
                {...field}
                inputId={name}
                value={hasValue ? field.value : null}
                unstyled
                menuPortalTarget={usePortal ? selectPortalProps.menuPortalTarget : undefined}
                menuPosition={usePortal ? selectPortalProps.menuPosition : undefined}
                styles={{
                  singleValue: (base) => ({
                    ...base,
                    overflow: 'visible',
                  }),
                  ...(usePortal && { menuPortal: selectPortalProps.menuPortalStyle }),
                }}
                isClearable
                aria-invalid={!!error || undefined}
                aria-required={required || undefined}
                aria-errormessage={error?.message ? errorId : undefined}
                aria-describedby={error?.message ? errorId : description ? helperId : undefined}
                components={{ Input: FormSelectInput }}
                onChange={(option) => {
                  field.onChange(option);
                  onChange?.(option as SingleValue<TOption>);
                }}
                isDisabled={disabled}
                cacheOptions
                defaultOptions
                noOptionsMessage={() => noOptionsMessage}
                loadingMessage={() => loadingMessage}
                loadOptions={(inputValue: string) => {
                  return inputValue.length >= MINIMUM_CHARACTERS_SEARCH || !minChars
                    ? debouncedLoadOptions(inputValue)
                    : Promise.resolve([]);
                }}
                onCreateOption={onCreateOption}
                isValidNewOption={
                  allowCreateWhileMatching ? (inputValue) => !!inputValue.trim() : undefined
                }
                classNames={SelectStyles(flagged, !!error)}
                formatOptionLabel={(option) => (
                  <div data-cy={`${CT.components.asyncCreateSelect.option}-${option.value}`}>
                    {option.label}
                  </div>
                )}
                formatCreateLabel={formatCreateLabel}
                filterOption={(option, inputValue) => matchesAllTokens(option.label, inputValue)}
              />
            </div>
          </FormField>
        );
      }}
    />
  );
};
